/**
 * Post-login Cognitory entry states on the existing main-page panel.
 * Reuses main-page visual tokens so selected Cognitory feels like the same product.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  submitCognitoryAnswer,
  type CognitoryAnswerCitationView,
  type CognitoryAnswerSupportDetails,
} from "../api/cognitoryAnswerClient";
import {
  fetchGovernedLibrarySource,
  openGovernedLibrarySourceBlob,
} from "../api/cognitoryLibrarySourceClient";
import { fetchGovernedLibraryDocuments } from "../api/cognitoryLibraryClient";
import {
  verifyCognitoryCitation,
  type CognitoryCitationVerificationResult,
} from "../api/cognitoryCitationVerificationClient";
import {
  createCognitoryAskThreadTurnId,
  buildConversationContextFromThread,
  loadCognitoryAskThread,
  saveCognitoryAskThread,
  type CognitoryAskThreadTurn,
} from "../entry/cognitoryAskThreadSession";
import CognitoryCitationSourcePanel from "./CognitoryCitationSourcePanel";
import CognitoryActivityShell from "./CognitoryActivityShell";
import CognitoryLibraryShell from "./CognitoryLibraryShell";
import CognitoryWorkspaceNav from "./CognitoryWorkspaceNav";
import CognitoryQuestionAnswerEvaluationCard from "./CognitoryQuestionAnswerEvaluationCard";
import { useCognitoryWorkspaceCapabilities } from "../entry/useCognitoryWorkspaceCapabilities";
import {
  useAuthorizedCognitoryAccess,
  type CognitoryAccessPhase,
} from "../entry/useAuthorizedCognitoryAccess";
import {
  hasProvisioningSetupDiscovery,
  isProvisioningSetupAccessUnverifiable,
  useProvisioningSetupAccessList,
  type ProvisioningSetupAccessListState,
} from "../entry/useProvisioningSetupAccessList";
import type { ProvisioningSetupAccessEntry } from "../api/cognitoryProvisioningSetupAccessClient";
import { buildCanonicalProvisioningSetupPath } from "../presentation/cognitoryProvisioningSetupNavigation";
import {
  parseRequestedCognitoryWorkspaceId,
  stripCognitoryWorkspaceRequestParam,
} from "../presentation/cognitoryWorkspaceNavigation";
import CognitoryAnswerMarkdown from "../presentation/CognitoryAnswerMarkdown";
import {
  buildCitationTitleLookupFromLibraryDocuments,
  findCitationByLabel,
  formatCitationListLabel,
  type CognitoryCitationTitleLookup,
} from "../presentation/cognitoryCitationPresentation";
import { shouldOfferGovernedCitationPdfHighlight } from "../presentation/cognitoryCitationPdfHighlightSession";
import { issueGovernedSourceViewSession } from "../api/cognitoryGovernedSourceViewSessionClient";
import { buildGovernedSourceViewPath } from "../presentation/cognitoryGovernedSourceViewNavigation";
import {
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_INPUT_STYLE,
  MAIN_PAGE_LIQUID_BUTTON_STYLE,
  MAIN_PAGE_PROCESSING_BUTTON_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
  MAIN_PAGE_SECTION_LABEL_STYLE,
  mainPageLiquidButtonHandlers,
} from "../theme/mainPageVisual";

function formatSupportDetailsBlock(
  details: CognitoryAnswerSupportDetails,
  requestId: string | null,
): string {
  const lines = [
    "Support details",
    `- Outcome: ${details.outcome}`,
    `- Reason: ${details.reason}`,
    `- Conversation context: ${details.conversationContext}`,
    `- Evidence search: ${details.evidenceSearch}`,
  ];
  if (details.matchingDocuments !== undefined) {
    lines.push(`- Matching documents: ${details.matchingDocuments}`);
  }
  if (details.documents && details.documents.length > 0) {
    lines.push(`- Documents: ${details.documents.join("; ")}`);
  }
  lines.push(`- Answer produced: ${details.answerProduced}`);
  lines.push(`- Support code: ${details.supportCode}`);
  if (requestId) {
    lines.push(`- Request ID: ${requestId}`);
  }
  return lines.join("\n");
}

function ProvisioningSetupDiscoverySection(input: {
  cognitories: ProvisioningSetupAccessEntry[];
}) {
  return (
    <section
      data-testid="cognitory-provisioning-setup-discovery"
      style={{
        marginTop: "1.25rem",
        paddingTop: "1rem",
        borderTop: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
      }}
    >
      <div
        style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.65rem" }}
      >
        CONTINUE SETUP
      </div>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gap: "0.55rem",
        }}
      >
        {input.cognitories.map(entry => (
          <li key={entry.cognitoryId}>
            <a
              href={buildCanonicalProvisioningSetupPath(entry.cognitoryId)}
              data-testid="cognitory-provisioning-setup-continue-link"
              data-cognitory-id={entry.cognitoryId}
              style={{
                ...MAIN_PAGE_QUIET_BUTTON_STYLE,
                display: "block",
                width: "100%",
                marginTop: 0,
                textAlign: "center",
                textDecoration: "none",
              }}
            >
              {entry.displayName}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProvisioningSetupDiscoveryBlock(input: {
  provisioning: ProvisioningSetupAccessListState;
}) {
  if (!hasProvisioningSetupDiscovery(input.provisioning)) {
    return null;
  }
  return (
    <ProvisioningSetupDiscoverySection
      cognitories={input.provisioning.cognitories}
    />
  );
}

function accessStatusLabel(phase: CognitoryAccessPhase): string {
  switch (phase) {
    case "resolving":
      return "Verifying Cognitory access…";
    case "one":
      return "Cognitory access verified.";
    case "multiple":
      return "Choose a Cognitory to continue.";
    case "none":
      return "No Cognitory assigned.";
    case "unable_to_verify":
      return "Unable to verify access.";
    case "temporary_failure":
      return "Unable to verify access right now.";
    case "unauthenticated":
      return "Sign-in session expired. Sign in again.";
    default:
      return "Checking Cognitory access…";
  }
}

function CognitoryWorkspaceShell(input: {
  cognitoryId: string;
  displayName: string;
  allowSwitch: boolean;
  onSwitch: () => void;
}) {
  const [mode, setMode] = useState<"ask" | "library" | "activity">("ask");
  const workspaceCapabilities = useCognitoryWorkspaceCapabilities({
    cognitoryId: input.cognitoryId,
    enabled: true,
  });
  const reloadWorkspaceCapabilities = workspaceCapabilities.reload;
  const handleEvaluationCollectionEnabledChange = useCallback(() => {
    reloadWorkspaceCapabilities();
  }, [reloadWorkspaceCapabilities]);
  const showActivity =
    workspaceCapabilities.capabilities?.canViewQuestionAnswerActivity ?? false;
  const canManageQuestionAnswerEvaluation =
    workspaceCapabilities.capabilities?.canManageQuestionAnswerEvaluation ??
    false;
  const evaluationCollectionEnabled =
    workspaceCapabilities.capabilities
      ?.questionAnswerEvaluationCollectionEnabled ?? false;
  const evaluationFeatureAvailable =
    workspaceCapabilities.capabilities
      ?.questionAnswerEvaluationFeatureAvailable ?? false;
  const [thread, setThread] = useState<CognitoryAskThreadTurn[]>(() =>
    loadCognitoryAskThread(input.cognitoryId),
  );

  useEffect(() => {
    saveCognitoryAskThread(input.cognitoryId, thread);
  }, [input.cognitoryId, thread]);

  return (
    <div data-testid="cognitory-workspace-shell">
      <p
        data-testid="cognitory-active-name"
        className="cognitory-panel-heading cognitory-workspace-heading"
        style={{
          fontSize: "clamp(1.25rem, 2.4vw, 1.55rem)",
          color: MAIN_PAGE_COLORS.inkStrong,
          marginBottom: "0.85rem",
          textAlign: "center",
          fontWeight: 400,
          letterSpacing: "-0.02em",
          fontFamily: MAIN_PAGE_FONT_STACK,
          lineHeight: 1.25,
        }}
      >
        {input.displayName}
      </p>
      {input.allowSwitch ? (
        <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
          <button
            type="button"
            data-testid="cognitory-switch"
            style={{ ...MAIN_PAGE_QUIET_BUTTON_STYLE, marginTop: 0 }}
            onClick={input.onSwitch}
          >
            Choose a different Cognitory
          </button>
        </div>
      ) : null}
      <CognitoryWorkspaceNav
        mode={mode}
        showActivity={showActivity}
        onSelectAsk={() => setMode("ask")}
        onSelectLibrary={() => setMode("library")}
        onSelectActivity={() => setMode("activity")}
      />
      {mode === "ask" ? (
        <CognitoryAskShell
          cognitoryId={input.cognitoryId}
          thread={thread}
          onThreadChange={setThread}
          evaluationCollectionEnabled={evaluationCollectionEnabled}
          evaluationFeatureAvailable={evaluationFeatureAvailable}
        />
      ) : null}
      {mode === "library" ? (
        <CognitoryLibraryShell cognitoryId={input.cognitoryId} />
      ) : null}
      {mode === "activity" && showActivity ? (
        <CognitoryActivityShell
          cognitoryId={input.cognitoryId}
          active={mode === "activity"}
          evaluationFeatureAvailable={evaluationFeatureAvailable}
          evaluationCollectionEnabled={evaluationCollectionEnabled}
          canManageQuestionAnswerEvaluation={canManageQuestionAnswerEvaluation}
          onEvaluationCollectionEnabledChange={
            handleEvaluationCollectionEnabledChange
          }
        />
      ) : null}
    </div>
  );
}

function CognitoryThreadTurnView(input: {
  turn: CognitoryAskThreadTurn;
  cognitoryId: string;
  evaluationCollectionEnabled: boolean;
  evaluationFeatureAvailable: boolean;
  activeCitationLabel: string | null;
  sourcePanelOpen: boolean;
  citationTitleLookup: CognitoryCitationTitleLookup;
  onOpenCitation: (citation: CognitoryAnswerCitationView) => void;
  onCitationMarkerClick: (citationLabel: string) => void;
}) {
  const { turn } = input;
  const citations = turn.citations ?? [];

  return (
    <article
      data-testid="cognitory-thread-turn"
      data-turn-id={turn.id}
      style={{ marginBottom: "1.25rem" }}
    >
      <div
        data-testid="cognitory-thread-question"
        style={{
          borderLeft: `3px solid ${MAIN_PAGE_COLORS.link}`,
          padding: "0.65rem 0.8rem",
          marginBottom: "0.65rem",
          background: "rgba(241, 245, 249, 0.75)",
          borderRadius: MAIN_PAGE_RADIUS.alert,
        }}
      >
        <p
          style={{
            ...MAIN_PAGE_SECTION_LABEL_STYLE,
            marginBottom: "0.35rem",
            fontSize: "0.78rem",
            letterSpacing: "0.1em",
          }}
        >
          YOU ASKED
        </p>
        <p
          style={{
            margin: 0,
            color: MAIN_PAGE_COLORS.inkStrong,
            fontFamily: MAIN_PAGE_FONT_STACK,
            fontSize: "0.98rem",
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {turn.question}
        </p>
      </div>

      {turn.kind === "error" ? (
        <p
          data-testid="cognitory-thread-error"
          role="alert"
          style={{
            margin: 0,
            color: MAIN_PAGE_COLORS.warnInk,
            background: MAIN_PAGE_COLORS.warnFill,
            border: `1px solid ${MAIN_PAGE_COLORS.warnBorder}`,
            borderRadius: MAIN_PAGE_RADIUS.alert,
            padding: "0.7rem 0.8rem",
            fontSize: "0.95rem",
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          {turn.errorMessage}
          {turn.requestId ? (
            <span
              data-testid="cognitory-answer-request-id"
              style={{ display: "block", marginTop: 6, opacity: 0.8 }}
            >
              Request ID: {turn.requestId}
            </span>
          ) : null}
        </p>
      ) : (
        <div
          data-testid="cognitory-thread-answer"
          data-cognitory-visual-shell="main-page"
        >
          <p style={MAIN_PAGE_SECTION_LABEL_STYLE}>
            {turn.kind === "refused" ? "Response" : "Answer"}
          </p>
          <div
            style={{
              marginBottom: citations.length > 0 ? "0.85rem" : 0,
            }}
          >
            <CognitoryAnswerMarkdown
              text={turn.answerText ?? ""}
              onCitationMarkerClick={input.onCitationMarkerClick}
            />
          </div>
          {turn.supportDetails ? (
            <pre
              data-testid="cognitory-support-details"
              style={{
                margin: "0.85rem 0 0",
                padding: "0.75rem 0.85rem",
                borderRadius: MAIN_PAGE_RADIUS.alert,
                border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                background: MAIN_PAGE_COLORS.panelFill,
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
                fontSize: "0.84rem",
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
              }}
            >
              {formatSupportDetailsBlock(turn.supportDetails, turn.requestId)}
            </pre>
          ) : null}
          {citations.length > 0 ? (
            <div data-testid="cognitory-citations-area">
              <p style={MAIN_PAGE_SECTION_LABEL_STYLE}>Citations</p>
              <ul
                style={{
                  listStyle: "none",
                  paddingLeft: 0,
                  margin: 0,
                  color: MAIN_PAGE_COLORS.slate,
                  fontFamily: MAIN_PAGE_FONT_STACK,
                  lineHeight: 1.5,
                  fontSize: "0.92rem",
                }}
              >
                {citations.map(citation => (
                  <li
                    key={`${turn.id}-${citation.citationLabel}`}
                    data-testid="cognitory-citation-item"
                    style={{
                      marginBottom: 6,
                      padding: "0.35rem 0.15rem",
                      borderBottom: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                    }}
                  >
                    <button
                      type="button"
                      data-testid="cognitory-citation-open"
                      onClick={() => input.onOpenCitation(citation)}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        color: MAIN_PAGE_COLORS.link,
                        cursor: "pointer",
                        fontFamily: MAIN_PAGE_FONT_STACK,
                        fontSize: "0.92rem",
                        lineHeight: 1.5,
                        textAlign: "left",
                      }}
                      aria-pressed={
                        input.activeCitationLabel === citation.citationLabel &&
                        input.sourcePanelOpen
                      }
                    >
                      <span data-testid="cognitory-citation-display-label">
                        {formatCitationListLabel(
                          citation,
                          input.citationTitleLookup,
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {turn.requestId ? (
            <p
              data-testid="cognitory-answer-request-id"
              style={{
                marginTop: "0.75rem",
                fontSize: "0.8rem",
                color: MAIN_PAGE_COLORS.whisper,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              Request ID: {turn.requestId}
            </p>
          ) : null}
          {turn.requestId &&
          (turn.kind === "answered" || turn.kind === "refused") ? (
            <CognitoryQuestionAnswerEvaluationCard
              cognitoryId={input.cognitoryId}
              traceRequestId={turn.requestId}
              collectionEnabled={input.evaluationCollectionEnabled}
              featureAvailable={input.evaluationFeatureAvailable}
            />
          ) : null}
        </div>
      )}
    </article>
  );
}

function CognitoryAskShell(input: {
  cognitoryId: string;
  thread: CognitoryAskThreadTurn[];
  onThreadChange: (thread: CognitoryAskThreadTurn[]) => void;
  evaluationCollectionEnabled: boolean;
  evaluationFeatureAvailable: boolean;
}) {
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sourcePanelOpen, setSourcePanelOpen] = useState(false);
  const [sourcePanelLoading, setSourcePanelLoading] = useState(false);
  const [sourcePanelResult, setSourcePanelResult] =
    useState<CognitoryCitationVerificationResult | null>(null);
  const [activeCitationLabel, setActiveCitationLabel] = useState<string | null>(
    null,
  );
  const [
    activeCitationVerificationReference,
    setActiveCitationVerificationReference,
  ] = useState<string | null>(null);
  const [openingSource, setOpeningSource] = useState(false);
  const [citationTitleLookup, setCitationTitleLookup] =
    useState<CognitoryCitationTitleLookup>({});

  const displayTurns = useMemo(
    () => [...input.thread].reverse(),
    [input.thread],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchGovernedLibraryDocuments({
        cognitoryId: input.cognitoryId,
      });
      if (cancelled || result.kind !== "listed") {
        return;
      }
      setCitationTitleLookup(
        buildCitationTitleLookupFromLibraryDocuments(result.documents),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [input.cognitoryId]);

  const closeSourcePanel = useCallback(() => {
    setSourcePanelOpen(false);
    setSourcePanelLoading(false);
    setSourcePanelResult(null);
    setActiveCitationLabel(null);
    setActiveCitationVerificationReference(null);
    setOpeningSource(false);
  }, []);

  const openCitationSource = useCallback(
    async (citation: CognitoryAnswerCitationView) => {
      setSourcePanelOpen(true);
      setSourcePanelLoading(true);
      setSourcePanelResult(null);
      setActiveCitationLabel(citation.citationLabel);
      setActiveCitationVerificationReference(
        typeof citation.verificationReference === "string"
          ? citation.verificationReference
          : null,
      );

      const result = await verifyCognitoryCitation({
        cognitoryId: input.cognitoryId,
        citation,
      });
      setSourcePanelLoading(false);
      setSourcePanelResult(result);
    },
    [input.cognitoryId],
  );

  const openActiveCitationSourceDocument = useCallback(async () => {
    if (
      sourcePanelResult?.kind !== "verified" ||
      !sourcePanelResult.sourceOpenEnabled ||
      !activeCitationVerificationReference
    ) {
      return;
    }
    const { documentId, documentVersionId } = sourcePanelResult;
    setOpeningSource(true);
    const result = await fetchGovernedLibrarySource({
      cognitoryId: input.cognitoryId,
      documentId,
      documentVersionId,
    });
    if (result.kind !== "opened") {
      setOpeningSource(false);
      return;
    }
    if (
      shouldOfferGovernedCitationPdfHighlight({
        contentType: result.contentType,
        pageNumber: sourcePanelResult.pageNumber ?? null,
        citedPassage: sourcePanelResult.citedPassage,
      })
    ) {
      const pageNumber = sourcePanelResult.pageNumber;
      if (pageNumber === undefined) {
        setOpeningSource(false);
        return;
      }
      const sessionResult = await issueGovernedSourceViewSession({
        cognitoryId: input.cognitoryId,
        citation: {
          verificationReference: activeCitationVerificationReference,
        },
      });
      setOpeningSource(false);
      if (sessionResult.kind !== "issued") {
        return;
      }
      window.open(
        buildGovernedSourceViewPath(
          input.cognitoryId,
          sessionResult.governedSession,
        ),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    setOpeningSource(false);
    openGovernedLibrarySourceBlob({
      blob: result.blob,
      contentType: result.contentType,
      ...(sourcePanelResult.pageNumber !== undefined
        ? { pageNumber: sourcePanelResult.pageNumber }
        : {}),
      citedPassage: sourcePanelResult.citedPassage,
    });
  }, [
    activeCitationVerificationReference,
    input.cognitoryId,
    sourcePanelResult,
  ]);

  const openCitationMarker = useCallback(
    (citationLabel: string, citations: CognitoryAnswerCitationView[]) => {
      const citation = findCitationByLabel(citations, citationLabel);
      if (!citation) {
        return;
      }
      void openCitationSource(citation);
    },
    [openCitationSource],
  );

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = question.trim();
      if (!trimmed || submitting) return;

      setQuestion("");
      setSubmitting(true);
      closeSourcePanel();

      const result = await submitCognitoryAnswer({
        cognitoryId: input.cognitoryId,
        question: trimmed,
        conversationContext: buildConversationContextFromThread(input.thread),
      });

      setSubmitting(false);

      const turnId = createCognitoryAskThreadTurnId();
      const authoritativeScope = result.conversationScope;

      if (result.kind === "answered") {
        input.onThreadChange([
          ...input.thread,
          {
            id: turnId,
            question: trimmed,
            kind: "answered",
            answerText: result.answerText,
            citations: result.citations,
            requestId: result.requestId,
            ...(authoritativeScope ? { authoritativeScope } : {}),
          },
        ]);
        return;
      }
      if (result.kind === "refused") {
        input.onThreadChange([
          ...input.thread,
          {
            id: turnId,
            question: trimmed,
            kind: "refused",
            answerText: result.refusalText,
            citations: [],
            requestId: result.requestId,
            ...(result.supportDetails
              ? { supportDetails: result.supportDetails }
              : {}),
            ...(authoritativeScope ? { authoritativeScope } : {}),
          },
        ]);
        return;
      }
      if (result.kind === "forbidden" || result.kind === "unauthenticated") {
        input.onThreadChange([
          ...input.thread,
          {
            id: turnId,
            question: trimmed,
            kind: "error",
            requestId: result.requestId,
            errorMessage: "Unable to verify access for this Cognitory.",
            ...(authoritativeScope ? { authoritativeScope } : {}),
          },
        ]);
        return;
      }
      if (result.kind === "answer_not_supported") {
        input.onThreadChange([
          ...input.thread,
          {
            id: turnId,
            question: trimmed,
            kind: "error",
            requestId: result.requestId,
            errorMessage:
              "Cognitory could not verify a citation-backed answer for this question.",
            ...(authoritativeScope ? { authoritativeScope } : {}),
          },
        ]);
        return;
      }
      if (result.kind === "temporary_failure") {
        input.onThreadChange([
          ...input.thread,
          {
            id: turnId,
            question: trimmed,
            kind: "error",
            requestId: result.requestId,
            errorMessage: "Temporary answer failure. Please try again.",
            ...(authoritativeScope ? { authoritativeScope } : {}),
          },
        ]);
        return;
      }
      input.onThreadChange([
        ...input.thread,
        {
          id: turnId,
          question: trimmed,
          kind: "error",
          requestId: result.requestId,
          errorMessage: "Temporary answer failure. Please try again.",
        },
      ]);
    },
    [closeSourcePanel, input, question, submitting],
  );

  return (
    <div
      data-testid="cognitory-ask-shell"
      data-cognitory-visual-shell="main-page"
      className="cognitory-ask-shell"
      style={{
        ...MAIN_PAGE_BODY_STYLE,
        marginTop: 0,
        textAlign: "left",
        color: MAIN_PAGE_COLORS.slate,
        lineHeight: 1.55,
      }}
    >
      <form
        data-testid="cognitory-ask-form"
        onSubmit={event => void onSubmit(event)}
      >
        <label
          htmlFor="cognitory-question"
          style={{
            display: "block",
            fontSize: "0.95rem",
            marginBottom: 6,
            color: MAIN_PAGE_COLORS.slate,
            fontFamily: MAIN_PAGE_FONT_STACK,
            textAlign: "left",
          }}
        >
          Ask a question
        </label>
        <textarea
          id="cognitory-question"
          data-testid="cognitory-question-input"
          className="cognitory-question-input"
          value={question}
          onChange={event => setQuestion(event.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Ask about your documents…"
          style={MAIN_PAGE_INPUT_STYLE}
        />
        <div style={{ textAlign: "center" }}>
          <button
            type="submit"
            data-testid="cognitory-question-submit"
            data-cognitory-submit-state={submitting ? "processing" : "idle"}
            className="cognitory-auth-liquid-btn"
            aria-busy={submitting}
            disabled={submitting || question.trim().length === 0}
            style={{
              ...(submitting
                ? MAIN_PAGE_PROCESSING_BUTTON_STYLE
                : MAIN_PAGE_LIQUID_BUTTON_STYLE),
              opacity: !submitting && question.trim().length === 0 ? 0.6 : 1,
              cursor:
                submitting || question.trim().length === 0
                  ? "not-allowed"
                  : "pointer",
            }}
            {...(submitting || question.trim().length === 0
              ? {}
              : mainPageLiquidButtonHandlers)}
          >
            {submitting ? "Checking…" : "Ask"}
          </button>
        </div>
      </form>

      {input.thread.length > 0 ? (
        <div
          data-testid="cognitory-thread"
          style={{ marginTop: "1.25rem", marginBottom: "0.25rem" }}
        >
          {displayTurns.map(turn => (
            <CognitoryThreadTurnView
              key={turn.id}
              turn={turn}
              cognitoryId={input.cognitoryId}
              evaluationCollectionEnabled={input.evaluationCollectionEnabled}
              evaluationFeatureAvailable={input.evaluationFeatureAvailable}
              activeCitationLabel={activeCitationLabel}
              sourcePanelOpen={sourcePanelOpen}
              citationTitleLookup={citationTitleLookup}
              onOpenCitation={citation => void openCitationSource(citation)}
              onCitationMarkerClick={citationLabel =>
                openCitationMarker(citationLabel, turn.citations ?? [])
              }
            />
          ))}
        </div>
      ) : null}

      <CognitoryCitationSourcePanel
        open={sourcePanelOpen}
        loading={sourcePanelLoading}
        result={sourcePanelResult}
        openingSource={openingSource}
        onClose={closeSourcePanel}
        onOpenSource={() => void openActiveCitationSourceDocument()}
      />
    </div>
  );
}

export default function CognitoryEntryExperience() {
  const [requestedCognitoryId] = useState(() =>
    parseRequestedCognitoryWorkspaceId(window.location.search),
  );
  const access = useAuthorizedCognitoryAccess({
    signedIn: true,
    requestedCognitoryId,
  });
  const provisioning = useProvisioningSetupAccessList({ signedIn: true });

  useEffect(() => {
    if (access.phase === "resolving" || !requestedCognitoryId) {
      return;
    }
    const { pathname, search, hash } = window.location;
    const cleaned = stripCognitoryWorkspaceRequestParam(pathname, search);
    const current = `${pathname}${search}`;
    if (cleaned !== current) {
      window.history.replaceState(
        window.history.state,
        "",
        `${cleaned}${hash}`,
      );
    }
  }, [access.phase, requestedCognitoryId]);

  if (access.selected) {
    return (
      <>
        <p
          data-testid="cognitory-auth-access-status"
          style={{
            fontSize: "0.92rem",
            marginBottom: "0.85rem",
            color: MAIN_PAGE_COLORS.muted,
            fontFamily: MAIN_PAGE_FONT_STACK,
            textAlign: "center",
          }}
        >
          Cognitory access verified
        </p>
        <CognitoryWorkspaceShell
          key={access.selected.cognitoryId}
          cognitoryId={access.selected.cognitoryId}
          displayName={access.selected.displayName}
          allowSwitch={access.cognitories.length > 1}
          onSwitch={access.clearSelection}
        />
        <ProvisioningSetupDiscoveryBlock provisioning={provisioning} />
      </>
    );
  }

  if (access.phase === "resolving") {
    return (
      <div
        data-testid="cognitory-access-resolving"
        data-cognitory-visual-shell="main-page"
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="cognitory-auth-access-status"
          style={{ textAlign: "center", marginBottom: 0 }}
        >
          Status: {accessStatusLabel("resolving")}
        </p>
      </div>
    );
  }

  if (access.phase === "multiple") {
    return (
      <div
        data-testid="cognitory-access-chooser"
        data-cognitory-visual-shell="main-page"
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="cognitory-auth-access-status"
          style={{ textAlign: "center", marginBottom: "0.85rem" }}
        >
          Status: {accessStatusLabel("multiple")}
        </p>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gap: "0.55rem",
          }}
        >
          {access.cognitories.map(entry => (
            <li key={entry.cognitoryId}>
              <button
                type="button"
                data-testid="cognitory-chooser-option"
                data-cognitory-id={entry.cognitoryId}
                style={{
                  ...MAIN_PAGE_QUIET_BUTTON_STYLE,
                  width: "100%",
                  marginTop: 0,
                  textAlign: "center",
                }}
                onClick={() => access.selectCognitory(entry.cognitoryId)}
              >
                {entry.displayName}
              </button>
            </li>
          ))}
        </ul>
        <ProvisioningSetupDiscoveryBlock provisioning={provisioning} />
      </div>
    );
  }

  if (access.phase === "none") {
    if (provisioning.phase === "resolving") {
      return (
        <div
          data-testid="cognitory-access-resolving"
          data-cognitory-visual-shell="main-page"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-auth-access-status"
            style={{ textAlign: "center", marginBottom: 0 }}
          >
            Status: Verifying Cognitory access…
          </p>
        </div>
      );
    }

    if (isProvisioningSetupAccessUnverifiable(provisioning)) {
      return (
        <div
          data-testid="cognitory-access-none-provisioning-unverified"
          data-cognitory-visual-shell="main-page"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-auth-access-status"
            style={{ textAlign: "center", marginBottom: "0.5rem" }}
          >
            Status: Unable to verify access.
          </p>
          <p
            style={{
              textAlign: "center",
              color: MAIN_PAGE_COLORS.soft,
              marginBottom: 0,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            Cognitory access could not be fully confirmed. Try again, or sign
            out and sign back in.
          </p>
          {provisioning.requestId ? (
            <p
              data-testid="cognitory-provisioning-access-request-id"
              style={{
                textAlign: "center",
                marginTop: "0.65rem",
                fontSize: "0.8rem",
                color: MAIN_PAGE_COLORS.whisper,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              Request ID: {provisioning.requestId}
            </p>
          ) : null}
          <div style={{ textAlign: "center" }}>
            <button
              type="button"
              data-testid="cognitory-provisioning-access-retry"
              style={MAIN_PAGE_QUIET_BUTTON_STYLE}
              onClick={provisioning.retry}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (hasProvisioningSetupDiscovery(provisioning)) {
      return (
        <div
          data-testid="cognitory-access-none-with-provisioning"
          data-cognitory-visual-shell="main-page"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-auth-access-status"
            style={{ textAlign: "center", marginBottom: "0.85rem" }}
          >
            Status: No active Cognitory assigned.
          </p>
          <p
            style={{
              textAlign: "center",
              marginBottom: 0,
              color: MAIN_PAGE_COLORS.soft,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            You have Cognitories ready for provisioning setup. Continue below to
            finish setup—nothing is broken, and nothing was created
            automatically.
          </p>
          <ProvisioningSetupDiscoverySection
            cognitories={provisioning.cognitories}
          />
        </div>
      );
    }

    return (
      <div
        data-testid="cognitory-access-none"
        data-cognitory-visual-shell="main-page"
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="cognitory-auth-access-status"
          style={{ textAlign: "center", marginBottom: "0.5rem" }}
        >
          Status: {accessStatusLabel("none")}
        </p>
        <p
          style={{
            textAlign: "center",
            marginBottom: 0,
            color: MAIN_PAGE_COLORS.soft,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Your account is signed in, but no Cognitory has been assigned yet.
          Access is set up by an administrator—nothing is broken, and nothing
          was created automatically.
        </p>
      </div>
    );
  }

  const failureLabel =
    access.phase === "temporary_failure"
      ? accessStatusLabel("temporary_failure")
      : access.phase === "unauthenticated"
        ? accessStatusLabel("unauthenticated")
        : accessStatusLabel("unable_to_verify");

  return (
    <div
      data-testid="cognitory-access-error"
      data-cognitory-visual-shell="main-page"
      style={MAIN_PAGE_BODY_STYLE}
    >
      <p
        data-testid="cognitory-auth-access-status"
        style={{ textAlign: "center", marginBottom: "0.5rem" }}
      >
        Status: {failureLabel}
      </p>
      <p
        style={{
          textAlign: "center",
          color: MAIN_PAGE_COLORS.soft,
          marginBottom: 0,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Cognitory access could not be confirmed. Try again, or sign out and sign
        back in.
      </p>
      {access.requestId ? (
        <p
          data-testid="cognitory-access-request-id"
          style={{
            textAlign: "center",
            marginTop: "0.65rem",
            fontSize: "0.8rem",
            color: MAIN_PAGE_COLORS.whisper,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Request ID: {access.requestId}
        </p>
      ) : null}
      <div style={{ textAlign: "center" }}>
        <button
          type="button"
          data-testid="cognitory-access-retry"
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          onClick={access.retry}
        >
          Try again
        </button>
      </div>
    </div>
  );
}
